"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UnitSystem = void 0;
/**
 * A unit system consists of a set of dimensions and corresponding base units. The base dimensions for a unit system
 * are defined by the keys of the Basis type parameter. The base units are given by the symbol map passed into the
 * constructor.
 */
var UnitSystem = /** @class */ (function () {
    function UnitSystem(symbols) {
        this.symbols = symbols;
        this.dimensions = Object.keys(symbols);
    }
    /**
     * Constructs a unit system given a map from dimension to the symbol for the base unit of that dimension. To
     * improve intellisense views for all safe-units types, make sure to pass an interface as a type argument to this
     * constructor.
     *
     * @param symbols a map from dimension to the unit symbol for the base unit of that dimension.
     * @example
     * const Basis = {
     *     length: "m",
     *     mass: "kg",
     *     time: "s",
     *     // ...
     * } as const;
     * type Basis = typeof Basis;
     * // Use an interface to type the basis so that intellisense views are simpler.
     * interface ExampleUnitSystem extends Basis {}
     *
     * // We pass the type parameter below even though it could be inferred.
     * const ExampleUnitSystem = UnitSystem.from<ExampleUnitSystem>({
     *     length: "m",
     *     mass: "kg",
     *     time: "s",
     *     ...
     * });
     */
    UnitSystem.from = function (symbols) {
        return new UnitSystem(symbols);
    };
    /** @returns the list of dimension names for this unit system. */
    UnitSystem.prototype.getDimensions = function () {
        return this.dimensions;
    };
    /** @returns the symbol of the base unit of a given dimension. */
    UnitSystem.prototype.getSymbol = function (dimension) {
        var symbol = this.symbols[dimension];
        if (typeof symbol === "string") {
            return symbol;
        }
        throw new Error("No symbol found for dimension: ".concat(String(dimension)));
    };
    /** @returns a unit representing a dimensionless value, all dimensions having an exponent of 0. */
    UnitSystem.prototype.createDimensionlessUnit = function () {
        return this.createUnit(function () { return 0; });
    };
    /** @returns a unit representing a given dimension, only that dimension having an exponent of 1. */
    UnitSystem.prototype.createDimensionUnit = function (dimension) {
        return this.createUnit(function (dim) { return (dim === dimension ? 1 : 0); });
    };
    /** @returns a unit that is the product of the two input units */
    UnitSystem.prototype.multiply = function (left, right) {
        return this.createUnit(function (dimension) { return left[dimension] + right[dimension]; });
    };
    /** @returns a unit that is the quotient of the two input units */
    UnitSystem.prototype.divide = function (left, right) {
        return this.createUnit(function (dimension) { return left[dimension] - right[dimension]; });
    };
    /** @returns a unit that is the reciprical of the input unit */
    UnitSystem.prototype.reciprocal = function (unit) {
        return this.createUnit(function (dimension) {
            var exponent = unit[dimension];
            return exponent === 0 ? 0 : -exponent;
        });
    };
    /**
     * Creates a unit by mapping each dimension of the unit system to a given exponent defined by a mapping function.
     *
     * @param getExponent a function which maps a given dimension of the unit system to the exponent of the resulting
     * unit.
     */
    UnitSystem.prototype.createUnit = function (getExponent) {
        var partial = {};
        for (var _i = 0, _a = this.dimensions; _i < _a.length; _i++) {
            var dimension = _a[_i];
            partial[dimension] = getExponent(dimension);
        }
        return partial;
    };
    return UnitSystem;
}());
exports.UnitSystem = UnitSystem;
//# sourceMappingURL=unitSystem.js.map